import { Period } from 'mbs-ui-shared@models/Period.enum';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { DefaultRetentionPeriodValues } from 'wizards/models/retention-policy-models';
import { isKeepBackupForCorrect, moreThatRetention } from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.utils';
import { CompanyBackupTemplate } from '../models/company-wizard-models';

export type TemplateRetentionProblem =
  | 'retentionPeriod'
  | 'ffiInterval'
  | 'objectLock'
  | 'gfsRetention'
  | 'gfsObjectLock'
  | 'gfsDefaultRetention';

export type TemplateProblem = TemplateRetentionProblem | 'source';

type RetentionPeriod = { count: number; period: Period };

const GFSLevels: ReadonlyArray<['weekly' | 'monthly' | 'yearly', Period]> = [
  ['weekly', Period.week],
  ['monthly', Period.month],
  ['yearly', Period.year]
];

export function templateProblems(template: CompanyBackupTemplate, connection: StorageConnection): TemplateProblem[] {
  const sourceMissing = !template.isImageBased && !template.whatBackupTreeStep?.includeFolders?.length;

  return [...(sourceMissing ? ['source' as const] : []), ...templateRetentionProblems(template, connection)];
}

export function templateRetentionProblems(template: CompanyBackupTemplate, connection: StorageConnection): TemplateRetentionProblem[] {
  const retention = template.destinationPlusRetention?.retentionPolicyStep;

  if (!retention) return [];

  const useDefault = !!retention.RetentionUseDefaultSettings;
  const keepFor: RetentionPeriod = useDefault
    ? {
        count: DefaultRetentionPeriodValues.deleteVersionsOlderThanCount,
        period: DefaultRetentionPeriodValues.deleteVersionsOlderThanPeriod
      }
    : { count: retention.deleteVersionsOlderThanCount, period: retention.deleteVersionsOlderThanPeriod };
  const objectLock: RetentionPeriod = connection?.ImmutabilityPeriodDays
    ? { count: connection.ImmutabilityPeriodDays, period: Period.day }
    : null;
  const gfs = retention.GFSPolicySettings;
  const gfsPeriods: RetentionPeriod[] = gfs?.enabled
    ? GFSLevels.filter(([level]) => gfs[level]?.enabled).map(([level, period]) => ({ count: gfs[level].storePeriod, period }))
    : [];
  const { ScheduleType, incrementalData } = template.scheduleStep ?? {};

  const problems: [TemplateRetentionProblem, boolean][] = [
    ['retentionPeriod', !useDefault && (!keepFor.count || keepFor.count < 1)],
    ['ffiInterval', ScheduleType === 'ffi' && !!incrementalData && isKeepBackupForCorrect(incrementalData, keepFor).error],
    ['objectLock', !!objectLock && moreThatRetention(objectLock, keepFor, false)],
    ['gfsRetention', gfsPeriods.some((value) => !moreThatRetention(value, keepFor))],
    ['gfsObjectLock', !!objectLock && gfsPeriods.some((value) => !moreThatRetention(value, objectLock))],
    ['gfsDefaultRetention', useDefault && !!gfs?.enabled]
  ];

  return problems.filter(([, failed]) => failed).map(([problem]) => problem);
}
